import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@availity/element';

import { useAppContext } from '@/context';
import { JOURNEY_EMAIL_VERIFICATION, checkNavigation } from '@/global';
import { useFRCallback, useFRStep } from '@/hooks';
import { EmailSent, ErrorPage, LoginStart, NewEmail, NullEmail, UnverifiedEmail, VerifiedEmail } from '@/pages';

const EmailVerification = () => {
  const { queryParams = {} } = useAppContext();

  const { authIndexValue = '' } = queryParams;

  // process authIndexValue in event that the UI should navigate to a different journey
  const journeyId = useMemo(() => authIndexValue?.toLowerCase(), [authIndexValue]);

  // call utility function to check if we need to navigate to a different route
  useEffect(() => {
    if (journeyId) {
      checkNavigation(journeyId);
    }
  }, [journeyId]);

  const [currentStep, setCurrentStep] = useState();
  const { stepToRender = {}, errorCode } = useFRStep(currentStep, JOURNEY_EMAIL_VERIFICATION);

  const { suspendedTextOutputCallback, payload } = useFRCallback(stepToRender);
  const { stage = '' } = payload;
  const formattedStage = stage?.toLowerCase();

  const pageNodeToRender = useMemo(() => {
    let output;
    if (stepToRender) {
      // need to check for suspended text output callback to render the email sent node; otherwise render page node from stage name.
      if (suspendedTextOutputCallback) {
        output = <EmailSent key="emailSent" stepToRender={stepToRender} />;
      } else if (formattedStage) {
        switch (formattedStage) {
          case 'loginpageunique001': {
            output = <LoginStart stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'unverifiedemail': {
            output = (
              <UnverifiedEmail key="unverifiedEmail" stepToRender={stepToRender} setCurrentStep={setCurrentStep} />
            );
            break;
          }
          case 'emailverified': {
            output = <VerifiedEmail key="verifiedEmail" stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
            break;
          }
          case 'noemail': {
            output = (
              <NullEmail
                key="noEmail"
                stepToRender={stepToRender}
                setCurrentStep={setCurrentStep}
                errorCode={errorCode}
              />
            );
            break;
          }
          case 'changeemail': {
            output = (
              <NewEmail
                key="newEmail"
                stepToRender={stepToRender}
                setCurrentStep={setCurrentStep}
                errorCode={errorCode}
              />
            );
            break;
          }
          case 'ErrorPage': {
            output = <ErrorPage key="errorPage" />;
            break;
          }
          default:
        }
      }
    }

    return output;
  }, [stepToRender, formattedStage, suspendedTextOutputCallback]);

  return stepToRender ? <Box role="main">{pageNodeToRender}</Box> : null;
};

export default EmailVerification;
